import type { AgentScreen, ContextUsage, HandoffChatEntry, HandoffResolution, RosterAgent, Routine, Skill, UpdateAgentRequest, Todo } from "@ark-bot/protocol";
import { useEffect, useState } from "react";

import { api } from "../api.js";
import { formatLocale, useText } from "../i18n.js";
import { DETAILS } from "../text/details.js";
import { useTimeZone } from "../time-zone.js";
import type { AgentCursor, ChatItem } from "../state.js";

import { AgentSettings } from "./AgentSettings.js";
import { Avatar } from "./Avatar.js";
import { Computer } from "./Computer.js";
import { Memory } from "./Memory.js";
import { MAX_MEMBERS } from "./NewChat.js";
import { Routines } from "./Routines.js";
import { Skills } from "./Skills.js";
import { Transcript } from "./Transcript.js";
import { ChannelsSummary, Conversations, WhatsAppPane } from "./WhatsApp.js";

/** What the pane is showing: the overview, or one of its parts opened up. */
export type DetailsMode = "details" | "profile" | "routines" | "skills" | "memory" | "conversation" | "whatsapp" | "contacts";

type Words = (typeof DETAILS)["en"];

const TITLE: Record<DetailsMode, (t: Words) => string> = {
  details: (t) => t.titleDetails,
  profile: (t) => t.titleSettings,
  routines: (t) => t.titleRoutines,
  skills: (t) => t.titleSkills,
  memory: (t) => t.titleMemory,
  conversation: (t) => t.titleConversation,
  whatsapp: (t) => t.titleWhatsApp,
  contacts: (t) => t.titleContacts,
};

interface Props {
  readonly agent: RosterAgent;
  /** Every agent, so a group's members can be edited from its pane. */
  readonly agents: readonly RosterAgent[];
  readonly mode: DetailsMode;
  readonly onMode: (mode: DetailsMode) => void;
  readonly onClose: () => void;
  readonly onSaveAgent: (request: UpdateAgentRequest) => Promise<void>;
  /** The outline: what the model was given and did. */
  readonly items: readonly ChatItem[];
  readonly busy: boolean;
  readonly routinesRevision: number;
  readonly skillsRevision: number;
  readonly todosRevision: number;
  readonly memoryRevision: number;
  /** Bumped when the agent's WhatsApp link or one of its threads moves (§6.19). */
  readonly channelsRevision: number;
  /** How full the agent's context is, for the gauge under the settings' slider. */
  readonly context: ContextUsage | null;
  /** The pane is on its way out; it plays its exit and is then taken down. */
  readonly closing?: boolean;
  /** The handle on the pane's left edge that drags it wider; it lives inside the pane so it sits on that edge wherever the pane is. */
  readonly resizer?: React.ReactNode;
  /** The screen card. */
  readonly screen: {
    readonly inUse: boolean;
    readonly cursor: AgentCursor | null;
    readonly screens: readonly AgentScreen[];
    readonly handoff: HandoffChatEntry | null;
    readonly onResolveHandoff: (resolution: HandoffResolution) => Promise<void>;
    /** The screen is open large over the page, so the card does not open it a second time. */
    readonly open: boolean;
    readonly onOpen: () => void;
    /** False on a host that serves no screen (a lite box, §6.17): the card is left out rather than shown empty. */
    readonly available: boolean;
  };
}

function when(ms: number | null, timeZone: string): string {
  if (ms == null) return "—";
  try {
    return new Intl.DateTimeFormat(formatLocale(), { timeZone, weekday: "short", hour: "2-digit", minute: "2-digit" }).format(new Date(ms));
  } catch {
    return new Date(ms).toLocaleString(formatLocale());
  }
}

/** The routines at a glance, as the prototype lists them under the screen; a row opens that routine's editor. */
function RoutinesSummary({ agentId, group, revision, onOpen, onCreate, onEdit }: { readonly agentId: string; readonly group: boolean; readonly revision: number; readonly onOpen: () => void; readonly onCreate: () => void; readonly onEdit: (id: string) => void }): React.JSX.Element {
  const t = useText(DETAILS);
  const [routines, setRoutines] = useState<Routine[] | null>(null);
  const { zone } = useTimeZone();
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    api
      .routines(agentId)
      .then((list) => {
        if (cancelled) return;
        setRoutines(list.routines);
        setError(null);
      })
      .catch((failure: unknown) => {
        if (!cancelled) setError(failure instanceof Error ? failure.message : String(failure));
      });
    return () => {
      cancelled = true;
    };
  }, [agentId, revision]);
  const empty = routines != null && routines.length === 0;
  return (
    <section className={`details-section${empty ? " routines-empty" : ""}`}>
      <div className="details-section-head">
        <h3>{t.titleRoutines}</h3>
        <button type="button" className="icon small" onClick={empty ? onCreate : onOpen} aria-label={empty ? t.createRoutineLabel : t.manageRoutines} title={empty ? t.createRoutineLabel : t.manageRoutines}>
          +
        </button>
      </div>
      {error != null ? <div className="settings-error">{error}</div> : null}
      {routines == null ? (
        <div className="muted">{t.loading}</div>
      ) : empty ? (
        <div className="routines-blank">
          <p className="muted">{t.routinesAbout(group)}</p>
          <button type="button" onClick={onCreate}>
            {t.createRoutine}
          </button>
        </div>
      ) : (
        <ul className="summary-list">
          {routines.map((routine) => (
            <li key={routine.id} className={`has-open${routine.enabled ? "" : " paused"}`}>
              <button type="button" className="summary-open" onClick={() => onEdit(routine.id)} aria-label={t.openRoutine(routine.name)}>
                <strong>{routine.name}</strong>
                <span className="muted">{routine.enabled ? t.nextRun(when(routine.nextRunAt, zone)) : t.paused}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** The bot's own task list for the job in hand, read-only: what is queued, underway and done. */
function TodosSummary({ agentId, revision }: { readonly agentId: string; readonly revision: number }): React.JSX.Element | null {
  const t = useText(DETAILS);
  const [todos, setTodos] = useState<Todo[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    api
      .todos(agentId)
      .then((list) => {
        if (!cancelled) setTodos(list.todos);
      })
      .catch(() => {
        if (!cancelled) setTodos([]);
      });
    return () => {
      cancelled = true;
    };
  }, [agentId, revision]);
  if (todos == null || todos.length === 0) return null;
  const mark = (status: Todo["status"]): string => (status === "done" ? "\u2713" : status === "in_progress" ? "\u25b6" : "\u25cb");
  return (
    <section className="details-section">
      <div className="details-section-head">
        <h3>{t.tasks}</h3>
        <span className="muted">{t.openTasks(todos.filter((todo) => todo.status !== "done").length)}</span>
      </div>
      <ul className="summary-list todos">
        {todos.map((todo) => (
          <li key={todo.id} className={`todo-${todo.status}`}>
            <span className="todo-mark" aria-hidden="true">{mark(todo.status)}</span>
            <span>{todo.text}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** The memory at a glance: how much the bot has kept, and the door to the browser. */
function MemorySummary({ agentId, revision, onOpen }: { readonly agentId: string; readonly revision: number; readonly onOpen: () => void }): React.JSX.Element {
  const t = useText(DETAILS);
  const [counts, setCounts] = useState<{ own: number; shared: number } | null>(null);
  useEffect(() => {
    let cancelled = false;
    api
      .memory(agentId)
      .then((list) => {
        if (!cancelled) setCounts({ own: list.facts.filter((fact) => fact.scope === "agent").length, shared: list.facts.filter((fact) => fact.scope === "user").length });
      })
      .catch(() => {
        if (!cancelled) setCounts(null);
      });
    return () => {
      cancelled = true;
    };
  }, [agentId, revision]);
  return (
    <section className="details-section">
      <div className="details-section-head">
        <h3>{t.titleMemory}</h3>
        <button type="button" className="linkish" onClick={onOpen}>
          {counts != null && counts.own + counts.shared > 0 ? t.browse : t.open}
        </button>
      </div>
      <p className="muted">{counts == null ? t.memoryAbout : counts.own + counts.shared === 0 ? t.memoryEmpty : t.memoryCounts(counts.own, counts.shared)}</p>
    </section>
  );
}

/** The skills at a glance: the names and when each is used. */
function SkillsSummary({ agentId, revision, onOpen }: { readonly agentId: string; readonly revision: number; readonly onOpen: () => void }): React.JSX.Element {
  const t = useText(DETAILS);
  const [skills, setSkills] = useState<Skill[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    api
      .skills(agentId)
      .then((list) => {
        if (cancelled) return;
        setSkills(list.skills);
        setError(null);
      })
      .catch((failure: unknown) => {
        if (!cancelled) setError(failure instanceof Error ? failure.message : String(failure));
      });
    return () => {
      cancelled = true;
    };
  }, [agentId, revision]);
  return (
    <section className="details-section">
      <div className="details-section-head">
        <h3>{t.titleSkills}</h3>
        <button type="button" className="linkish" onClick={onOpen}>
          {skills != null && skills.length > 0 ? t.manage : t.writeSkill}
        </button>
      </div>
      {error != null ? <div className="settings-error">{error}</div> : null}
      {skills == null ? (
        <div className="muted">{t.loading}</div>
      ) : skills.length === 0 ? (
        <p className="muted">{t.skillsAbout}</p>
      ) : (
        <ul className="summary-list">
          {skills.map((skill) => (
            <li key={skill.id}>
              <strong>{skill.name}</strong>
              <span className="muted">{skill.description}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * A group's members, as the prototype lists them: each with its face, a
 * Remove on hover, and Add member underneath opening a list of who else
 * could join. Every change is saved at once; there is no form.
 */
function Members({ group, agents, onSave }: { readonly group: RosterAgent; readonly agents: readonly RosterAgent[]; readonly onSave: (request: UpdateAgentRequest) => Promise<void> }): React.JSX.Element {
  const t = useText(DETAILS);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const members = group.members.map((id) => agents.find((agent) => agent.id === id)).filter((agent): agent is RosterAgent => agent != null);
  const others = agents.filter((agent) => agent.kind !== "group" && !group.members.includes(agent.id));

  const save = async (next: readonly string[]): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await onSave({ members: [...next] });
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="details-section members-section">
      <div className="details-section-head">
        <h3>{t.members}</h3>
        <span className="muted">
          {members.length}/{MAX_MEMBERS}
        </span>
      </div>
      <ul className="member-list">
        {members.map((member) => (
          <li key={member.id} className="member-row">
            <Avatar id={member.id} name={member.name} avatar={member.config.avatar} size={34} />
            <span className="member-body">
              <strong>{member.name}</strong>
              {member.config.title != null && member.config.title.trim().length > 0 ? <span className="muted">{member.config.title}</span> : null}
            </span>
            <button type="button" className="member-remove" disabled={busy} onClick={() => void save(group.members.filter((id) => id !== member.id))} aria-label={t.removeFromGroup(member.name)}>
              {t.remove}
            </button>
          </li>
        ))}
        {members.length === 0 ? <li className="hint muted">{t.noMembers}</li> : null}
      </ul>
      {adding ? (
        <div className="member-picker popover-inline">
          {others.length === 0 ? (
            <div className="hint muted">{t.everyBotIn}</div>
          ) : (
            others.map((agent) => (
              <button
                key={agent.id}
                type="button"
                className="pick-row"
                disabled={busy || group.members.length >= MAX_MEMBERS}
                onClick={() => {
                  setAdding(false);
                  void save([...group.members, agent.id]);
                }}
              >
                <Avatar id={agent.id} name={agent.name} avatar={agent.config.avatar} size={30} />
                <span className="pick-body">
                  <strong>{agent.name}</strong>
                  {agent.config.title != null && agent.config.title.trim().length > 0 ? <span className="muted">{agent.config.title}</span> : null}
                </span>
              </button>
            ))
          )}
        </div>
      ) : null}
      <button type="button" className="member-add" onClick={() => setAdding((open) => !open)} aria-expanded={adding} disabled={busy || group.members.length >= MAX_MEMBERS} title={group.members.length >= MAX_MEMBERS ? t.groupFull(MAX_MEMBERS) : t.addBotToGroup}>
        <span className="member-add-icon" aria-hidden="true">
          +
        </span>
        {t.addMember}
      </button>
      {error != null ? <div className="settings-error">{error}</div> : null}
    </section>
  );
}

/**
 * The pane beside the chat, as the prototype's: the agent's screen at the
 * top, then its routines and skills, with its settings behind the gear and
 * the full conversation one tap away. A group shows its face, name and
 * description on top, then its members, then its routines. One part opened
 * up fills the pane until it is closed again.
 */
export function Details({ agent, agents, mode, onMode, onClose, onSaveAgent, items, busy, routinesRevision, skillsRevision, todosRevision, memoryRevision, channelsRevision, context, closing = false, resizer, screen }: Props): React.JSX.Element {
  const t = useText(DETAILS);
  const group = agent.kind === "group";
  /** The routines editor opens straight on a blank form when the summary's "Create routine" was pressed. */
  const [createRoutine, setCreateRoutine] = useState(false);
  /** The routine the summary row asked to edit, if any. */
  const [editRoutineId, setEditRoutineId] = useState<string | null>(null);
  /** The WhatsApp conversation open in the conversations list, if one is. */
  const [threadId, setThreadId] = useState<string | null>(null);
  useEffect(() => setThreadId(null), [agent.id]);
  // Back from a conversation is the list; from the list, the WhatsApp pane it was opened from.
  const back = (): void => {
    if (mode === "contacts" && threadId != null) setThreadId(null);
    else onMode("details");
  };
  const openRoutines = (create: boolean, editId: string | null = null): void => {
    setCreateRoutine(create);
    setEditRoutineId(editId);
    onMode("routines");
  };
  return (
    <aside className={`details-pane mode-${mode}${closing ? " closing" : ""}`} aria-label={TITLE[mode](t)}>
      {closing ? null : resizer}
      <div className="details-head">
        {mode === "details" ? null : (
          <button type="button" className="icon" onClick={back} aria-label={t.backToDetails} title={t.back}>
            ‹
          </button>
        )}
        <span className="details-title">{TITLE[mode](t)}</span>
        {mode === "profile" ? null : (
          <button type="button" className="icon" onClick={() => onMode("profile")} aria-label={t.titleSettings} title={t.settingsTitle}>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
            </svg>
          </button>
        )}
        <button type="button" className="icon" onClick={onClose} aria-label={t.hideDetails} title={t.hidePane}>
          <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M5 5l5 5-5 5M10.5 5l5 5-5 5" />
          </svg>
        </button>
      </div>
      {mode === "profile" ? <AgentSettings key={agent.id} agent={agent} context={context} busy={busy} onSave={onSaveAgent} onClose={back} /> : null}
      {mode === "routines" ? <Routines key={`routines-${agent.id}`} agentId={agent.id} agentName={agent.name} revision={routinesRevision} startCreating={createRoutine} startEditing={editRoutineId} onClose={back} /> : null}
      {mode === "skills" ? <Skills key={`skills-${agent.id}`} agentId={agent.id} agentName={agent.name} revision={skillsRevision} onClose={back} /> : null}
      {mode === "memory" ? <Memory key={`memory-${agent.id}`} agentId={agent.id} agentName={agent.name} revision={memoryRevision} onClose={back} /> : null}
      {mode === "conversation" ? <Transcript items={items} busy={busy} /> : null}
      {mode === "whatsapp" ? <WhatsAppPane key={`whatsapp-${agent.id}`} agentId={agent.id} agentName={agent.name} revision={channelsRevision} onOpenConversations={() => onMode("contacts")} /> : null}
      {mode === "contacts" ? <Conversations key={`contacts-${agent.id}`} agentId={agent.id} revision={channelsRevision} threadId={threadId} onThread={setThreadId} /> : null}
      {mode === "details" ? (
        <div className="details-body">
          {/* The head of the pane is the screen where there is one. A room has
              no box of its own, and a lite host has no desktop at all (§6.17);
              either way the agent's own face and description take that place
              rather than an empty card. */}
          {group || !screen.available ? (
            <section className="details-section group-head">
              <Avatar id={agent.id} name={agent.name} avatar={agent.config.avatar} size={88} />
              <h2 className="group-name">{agent.name}</h2>
              {agent.config.description != null && agent.config.description.trim().length > 0 ? <p className="group-description muted">{agent.config.description}</p> : <p className="group-description muted">{t.noDescription}</p>}
            </section>
          ) : (
            <section className="details-section screen-section">
              {screen.open ? (
                <div className="screen-card placeholder">
                  <span className="muted">{t.openLarge}</span>
                </div>
              ) : (
                <Computer variant="card" agentName={agent.name} inUse={screen.inUse} cursor={screen.cursor} screens={screen.screens} handoff={screen.handoff} onResolveHandoff={screen.onResolveHandoff} onClose={onClose} onExpand={screen.onOpen} />
              )}
              <div className="screen-caption muted">
                {t.screenOf(agent.name)}{screen.inUse ? t.inUse : ""}
              </div>
            </section>
          )}
          {group ? <Members group={agent} agents={agents} onSave={onSaveAgent} /> : null}
          {/* Under the agent's face and above its routines, where the prototype's Agent info keeps Channels (§6.19). A room has no number. */}
          {group ? null : <ChannelsSummary agentId={agent.id} agentName={agent.name} revision={channelsRevision} onOpen={() => onMode("whatsapp")} onOpenConversations={() => onMode("contacts")} />}
          <RoutinesSummary agentId={agent.id} group={group} revision={routinesRevision} onOpen={() => openRoutines(false)} onCreate={() => openRoutines(true)} onEdit={(id) => openRoutines(false, id)} />
          {group ? null : (
            <>
              <TodosSummary agentId={agent.id} revision={todosRevision} />
              <SkillsSummary agentId={agent.id} revision={skillsRevision} onOpen={() => onMode("skills")} />
              <MemorySummary agentId={agent.id} revision={memoryRevision} onOpen={() => onMode("memory")} />
              <section className="details-section">
                <div className="details-section-head">
                  <h3>{t.titleConversation}</h3>
                  <button type="button" className="linkish" onClick={() => onMode("conversation")}>
                    {t.show}
                  </button>
                </div>
                <p className="muted">{t.conversationAbout}</p>
              </section>
            </>
          )}
        </div>
      ) : null}
    </aside>
  );
}
